import {
  ProviderEvents,
  StandardResolutionReasons,
} from '@openfeature/web-sdk';
import type { Provider, ResolutionDetails } from '@openfeature/web-sdk';

/**
 * An OpenFeature {@link Provider} augmented with local, session-only flag
 * overrides. Behaves identically to the wrapped provider except that overridden
 * flags return the override value from every `resolve*Evaluation` call.
 */
export type OverridableProvider = Provider & {
  /** Override a flag's value locally and notify subscribers. */
  setOverride: (flagKey: string, value: unknown) => void;
  /** Remove a single override, reverting the flag to the real provider value. */
  clearOverride: (flagKey: string) => void;
  /** Remove all local overrides. */
  clearAllOverrides: () => void;
  /** The current local overrides as a plain object. */
  getOverrides: () => Record<string, unknown>;
};

const VARIATION_METHODS = new Set([
  'resolveBooleanEvaluation',
  'resolveStringEvaluation',
  'resolveNumberEvaluation',
  'resolveObjectEvaluation',
]);

/**
 * Wraps an OpenFeature provider so flag values can be overridden locally,
 * session-only — for simulating flags on/off from the debug panel without
 * touching the Datadog dashboard. Provider-agnostic: works over any
 * {@link Provider}, so it isn't Datadog-specific.
 *
 * Overrides take precedence in the four `resolve*Evaluation` methods; all other
 * members delegate to the wrapped provider. Setting or clearing an override
 * emits {@link ProviderEvents.ConfigurationChanged} on the provider's event
 * emitter, so OpenFeature re-evaluates and subscribed hooks re-render.
 *
 * @param provider - The real provider to wrap (e.g. `DatadogProvider`).
 * @returns The provider augmented with the override controls in
 *   {@link OverridableProvider}.
 */
export const withOverrides = (provider: Provider): OverridableProvider => {
  const overrides = new Map<string, unknown>();
  const notify = () =>
    provider.events?.emit(ProviderEvents.ConfigurationChanged, {
      message: 'local flag override changed',
    });

  return new Proxy(provider, {
    get(target, prop, receiver) {
      switch (prop) {
        case 'setOverride':
          return (flagKey: string, value: unknown) => {
            overrides.set(flagKey, value);
            notify();
          };
        case 'clearOverride':
          return (flagKey: string) => {
            overrides.delete(flagKey);
            notify();
          };
        case 'clearAllOverrides':
          return () => {
            overrides.clear();
            notify();
          };
        case 'getOverrides':
          return () => Object.fromEntries(overrides);
        default: {
          if (typeof prop === 'string' && VARIATION_METHODS.has(prop)) {
            return (
              flagKey: string,
              defaultValue: unknown,
              context?: unknown,
              logger?: unknown,
            ): ResolutionDetails<unknown> =>
              overrides.has(flagKey)
                ? {
                    value: overrides.get(flagKey),
                    reason: StandardResolutionReasons.STATIC,
                  }
                : (
                    target as unknown as Record<
                      string,
                      (...args: unknown[]) => ResolutionDetails<unknown>
                    >
                  )[prop](flagKey, defaultValue, context, logger);
          }
          const value = Reflect.get(target, prop, receiver);
          return typeof value === 'function' ? value.bind(target) : value;
        }
      }
    },
  }) as unknown as OverridableProvider;
};
